import { useState, useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Clock, Calendar } from 'lucide-react';
import { api } from '../api';

const ACCENT = '#5E0ED7';
const ease = [0.22, 1, 0.36, 1];

export default function BlogPost() {
  const { id } = useParams();
  const location = useLocation();
  const [post, setPost]       = useState(location.state?.post || null);
  const [loading, setLoading] = useState(!location.state?.post);
  const [error, setError]     = useState('');

  useEffect(() => {
    // If we already have post from router state, skip API call
    if (location.state?.post) return;
    api.getBlogPost(id)
      .then(setPost)
      .catch(() => setError('Post not found'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#FAFAF8' }}>
      <div className="w-8 h-8 border-2 border-gray-200 rounded-full animate-spin" style={{ borderTopColor: ACCENT }} />
    </div>
  );

  if (error || !post) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4" style={{ background: '#FAFAF8' }}>
      <p className="text-gray-500">Post not found.</p>
      <Link to="/#blog" className="text-sm font-semibold" style={{ color: ACCENT }}>← Back to Blog</Link>
    </div>
  );

  return (
    <div style={{ background: '#FAFAF8', minHeight: '100dvh', fontFamily: "'Inter', sans-serif" }}>
      {/* Nav */}
      <div className="sticky top-0 z-40 px-5 sm:px-8 md:px-12 py-4 border-b" style={{ background: 'rgba(250,250,248,0.92)', backdropFilter: 'blur(20px)', borderColor: 'rgba(24,24,27,0.08)' }}>
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-sm font-semibold" style={{ color: '#18181B' }}>
            <img src="/logo.png" alt="Syenovix" className="w-7 h-7 rounded-lg object-cover" />
            <span className="hidden sm:block tracking-widest uppercase" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Syenovix</span>
          </Link>
          <Link to="/#blog" className="flex items-center gap-1.5 text-sm font-semibold" style={{ color: ACCENT }}>
            <ArrowLeft size={14} /> Back to Blog
          </Link>
        </div>
      </div>

      {/* Article */}
      <article className="max-w-4xl mx-auto px-5 sm:px-8 py-16">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease }}
        >
          {/* Category */}
          {post.category && (
            <span
              className="inline-block text-[10px] font-semibold tracking-[0.12em] uppercase px-3 py-1 rounded-full mb-6"
              style={{ background: 'rgba(94,14,215,0.08)', color: ACCENT, border: '1px solid rgba(94,14,215,0.14)' }}
            >
              {post.category}
            </span>
          )}

          {/* Title */}
          <h1
            className="text-4xl sm:text-5xl font-bold tracking-tight leading-[1.08] mb-6"
            style={{ color: '#18181B', fontFamily: "'Plus Jakarta Sans', sans-serif", letterSpacing: '-0.025em' }}
          >
            {post.title}
          </h1>

          {/* Meta */}
          <div className="flex items-center gap-5 mb-10 pb-8" style={{ borderBottom: '1px solid rgba(24,24,27,0.08)' }}>
            <div className="flex items-center gap-1.5 text-sm" style={{ color: '#A0A0A7' }}>
              <Calendar size={13} />
              {new Date(post.created_at || post.date || Date.now()).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </div>
            <div className="flex items-center gap-1.5 text-sm" style={{ color: '#A0A0A7' }}>
              <Clock size={13} />
              {post.read_time || post.readTime || 5} min read
            </div>
            <span className="text-sm" style={{ color: '#A0A0A7' }}>By {post.author || 'Syenovix'}</span>
          </div>

          {/* Content */}
          <div
            className="prose prose-lg max-w-none"
            style={{ color: '#3f3f46', lineHeight: 1.8 }}
            dangerouslySetInnerHTML={{ __html: post.content?.replace(/\n/g, '<br/>') || post.excerpt }}
          />
        </motion.div>

        {/* CTA */}
        <div
          className="mt-16 rounded-2xl p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
          style={{ background: 'rgba(94,14,215,0.05)', border: '1px solid rgba(94,14,215,0.10)' }}
        >
          <div>
            <p className="font-semibold" style={{ color: '#18181B', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Want to work with us?</p>
            <p className="text-sm mt-1" style={{ color: '#6B6B72' }}>Let's discuss your project.</p>
          </div>
          <Link
            to="/#contact"
            className="flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white shrink-0"
            style={{ background: ACCENT }}
          >
            Get in Touch
          </Link>
        </div>
      </article>
    </div>
  );
}
